import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Select from 'react-select';
import axios from 'axios';

function UserModal({ isOpen, onClose, onSave, user, mode, departments, workingPolicies, users, canEditAttendanceExclusion = false }) {
  const emptyAddress = {
    type: 'current',
    houseNo: '',
    street: '',
    landmark: '',
    city: '',
    state: '',
    country: '',
    pincode: ''
  };
  const emptyWorkHistory = { companyName: '', designation: '', fromDate: '', toDate: '', description: '' };
  const emptyEducation = { degree: '', institution: '', fieldOfStudy: '', startYear: '', endYear: '' };
  const emptyRoleHistory = { designation: '', role: '', businessUnit: '', fromDate: '', toDate: '', notes: '' };
  const emptyManagerHistory = { managerId: '', fromDate: '', toDate: '' };
  const emptyDocument = { type: 'aadhaar_front', fileUrl: '', mimeType: '', fileSize: '' };

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    officialEmail: '',
    mobileNumber: '',
    alternateEmail: '',
    organizationId: '',
    uidNumber: '',
    employeeCode: '',
    profilePhotoUrl: '',
    designation: '',
    role: '',
    businessUnit: '',
    company: '',
    managerId: '',
    orgChartReference: '',
    dateOfJoining: '',
    employeeSubType: '',
    currentOfficeCountry: '',
    currentOfficeState: '',
    currentOfficeCity: '',
    gender: '',
    dateOfBirth: '',
    nationality: '',
    fatherName: '',
    bloodGroup: '',
    languagesSpoken: '',
    personalEmail: '',
    personalMobileNumber: '',
    officeMobileNumber: '',
    linkedinUrl: '',
    mobileAccess: false,
    userStatus: 'Active',
    Department: '',
    workingHourPolicyId: '',
    employementType: 'Full-Time',
    isTeamLead: false,
    isManager: false,
    excludeFromAttendanceStats: false,
    latitude: '',
    longitude: '',
    locationRadius: '',
    addresses: {
      current: { ...emptyAddress, type: 'current' },
      permanent: { ...emptyAddress, type: 'permanent' },
      sameAsCurrent: false
    },
    emergencyContacts: [{ name: '', phone: '', relation: '' }],
    workHistory: [{ ...emptyWorkHistory }],
    education: [{ ...emptyEducation }],
    roleHistory: [{ ...emptyRoleHistory }],
    managerHistory: [{ ...emptyManagerHistory }],
    identity: {
      aadhaarLast4: '',
      panNumber: '',
      passportNumber: '',
      epfNumber: '',
      uanNumber: '',
      pranNumber: ''
    },
    documents: [{ ...emptyDocument }]
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [showManagerSection, setShowManagerSection] = useState(false);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [profilePhotoMode, setProfilePhotoMode] = useState('url');
  const [profilePhotoFileName, setProfilePhotoFileName] = useState('');
  const [profilePhotoUploading, setProfilePhotoUploading] = useState(false);
  const [profilePhotoUploadError, setProfilePhotoUploadError] = useState('');
  const [teamLeadBlockOpen, setTeamLeadBlockOpen] = useState(false);
  const [teamLeadBlockSubordinates, setTeamLeadBlockSubordinates] = useState([]);
  const [teamLeadBlockLoading, setTeamLeadBlockLoading] = useState(false);
  const [teamLeadBlockError, setTeamLeadBlockError] = useState('');

  const tabs = [
    { id: 'overview', label: 'Profile Overview' },
    { id: 'personal', label: 'Personal' },
    { id: 'contact', label: 'Contact' },
    { id: 'address', label: 'Address' },
    { id: 'employment', label: 'Employment' },
    { id: 'experience', label: 'Experience' },
    { id: 'education', label: 'Education' },
    { id: 'identity', label: 'Identity' },
    { id: 'documents', label: 'Documents' },
    { id: 'emergency', label: 'Emergency' }
  ];
  const [newManagerPermissions, setNewManagerPermissions] = useState({
    canViewCheckoutRequests: true,
    canViewLeaveRequests: true,
    canViewTeamAttendance: true,
    canViewPerformance: true,
    canViewWorkload: true
  });

  const mapUserToFormData = (userData) => {
    const currentAddress = userData?.addresses?.find(a => a.type === 'current') || { ...emptyAddress, type: 'current' };
    const permanentAddress = userData?.addresses?.find(a => a.type === 'permanent') || { ...emptyAddress, type: 'permanent' };

    return {
      firstName: userData?.firstName || '',
      lastName: userData?.lastName || '',
      officialEmail: userData?.officialEmail || '',
      mobileNumber: userData?.mobileNumber || '',
      alternateEmail: userData?.alternateEmail || '',
      organizationId: userData?.organizationId || users?.[0]?.organizationId || '',
      uidNumber: userData?.uidNumber || '',
      employeeCode: userData?.employeeCode || '',
      profilePhotoUrl: userData?.profilePhotoUrl || '',
      designation: userData?.designation || '',
      role: userData?.role || '',
      businessUnit: userData?.businessUnit || '',
      company: userData?.company || '',
      managerId: userData?.managerId || '',
      orgChartReference: userData?.orgChartReference || '',
      dateOfJoining: userData?.dateOfJoining || '',
      employeeSubType: userData?.employeeSubType || '',
      currentOfficeCountry: userData?.currentOfficeCountry || '',
      currentOfficeState: userData?.currentOfficeState || '',
      currentOfficeCity: userData?.currentOfficeCity || '',
      gender: userData?.gender || '',
      dateOfBirth: userData?.dateOfBirth || '',
      nationality: userData?.nationality || '',
      fatherName: userData?.fatherName || '',
      bloodGroup: userData?.bloodGroup || '',
      languagesSpoken: Array.isArray(userData?.languagesSpoken)
        ? userData.languagesSpoken.join(', ')
        : (userData?.languagesSpoken || ''),
      personalEmail: userData?.personalEmail || '',
      personalMobileNumber: userData?.personalMobileNumber || '',
      officeMobileNumber: userData?.officeMobileNumber || '',
      linkedinUrl: userData?.linkedinUrl || '',
      mobileAccess: Boolean(userData?.mobileAccess),
      userStatus: userData?.userStatus || 'Active',
      Department: userData?.Department || '',
      workingHourPolicyId: userData?.workingHourPolicyId || '',
      employementType: userData?.employementType || 'Full-Time',
      isTeamLead: userData?.isTeamLead || false,
      isManager: userData?.isManager || false,
      excludeFromAttendanceStats: Boolean(userData?.excludeFromAttendanceStats),
      latitude: userData?.latitude !== null && userData?.latitude !== undefined ? userData.latitude.toString() : '',
      longitude: userData?.longitude !== null && userData?.longitude !== undefined ? userData.longitude.toString() : '',
      locationRadius: userData?.locationRadius !== null && userData?.locationRadius !== undefined ? userData.locationRadius.toString() : '',
      addresses: {
        current: { ...currentAddress },
        permanent: { ...permanentAddress },
        sameAsCurrent: false
      },
      emergencyContacts: userData?.emergencyContacts?.length ? userData.emergencyContacts : [{ name: '', phone: '', relation: '' }],
      workHistory: userData?.workHistory?.length ? userData.workHistory : [{ ...emptyWorkHistory }],
      education: userData?.education?.length ? userData.education : [{ ...emptyEducation }],
      roleHistory: userData?.roleHistory?.length ? userData.roleHistory : [{ ...emptyRoleHistory }],
      managerHistory: userData?.managerHistory?.length ? userData.managerHistory : [{ ...emptyManagerHistory }],
      identity: {
        aadhaarLast4: userData?.identity?.aadhaarLast4 || '',
        panNumber: userData?.identity?.panNumber || '',
        passportNumber: userData?.identity?.passportNumber || '',
        epfNumber: userData?.identity?.epfNumber || '',
        uanNumber: userData?.identity?.uanNumber || '',
        pranNumber: userData?.identity?.pranNumber || ''
      },
      documents: userData?.documents?.length ? userData.documents : [{ ...emptyDocument }]
    };
  };

  useEffect(() => {
    if (isOpen) {
      console.log('UserModal opened with departments:', departments);
      setActiveTab('overview');
      if (mode === 'edit' && user?.id) {
        setLoadingUserDetails(true);
        const token = localStorage.getItem('token');
        axios.get(`/api/users/${user.id}`, { headers: { Authorization: `Bearer ${token}` } })
          .then((response) => {
            setFormData(mapUserToFormData(response.data));
            setProfilePhotoMode(response.data?.profilePhotoUrl ? 'url' : 'url');
            setProfilePhotoFileName('');
            loadUserManagers(user.id);
          })
          .catch(() => {
            setFormData(mapUserToFormData(user));
            setProfilePhotoMode(user?.profilePhotoUrl ? 'url' : 'url');
            setProfilePhotoFileName('');
            loadUserManagers(user.id);
          })
          .finally(() => {
            setLoadingUserDetails(false);
          });
      } else {
        setFormData(mapUserToFormData(user || {}));
        setProfilePhotoMode('url');
        setProfilePhotoFileName('');
        setManagers([]);
      }
      setErrors({});
    }
  }, [isOpen, mode, user]);

  const loadUserManagers = async (userId) => {
    try {
      setLoadingManagers(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`/api/managers/user/${userId}/managers`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setManagers(response.data.managers || []);
      }
    } catch (error) {
      console.error('Error loading managers:', error);
      console.error('Full error:', error.response?.data || error.message);
    } finally {
      setLoadingManagers(false);
    }
  };

  const handleAddManager = async (managerId) => {
    try {
      if (!user?.id) {
        alert('User ID is required. Please save the user first.');
        return;
      }
      
      const token = localStorage.getItem('token');
      
      console.log('Adding manager:', managerId, 'to user:', user.id);
      
      const response = await axios.post(
        `/api/managers/user/${user.id}/managers`,
        {
          managerId,
          isPrimary: managers.length === 0, // First manager is primary
          relationship: 'Direct',
          permissions: newManagerPermissions
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('Manager added successfully:', response.data);
      
      // Reload managers
      await loadUserManagers(user.id);
      
      // Reset permissions to default
      setNewManagerPermissions({
        canViewCheckoutRequests: true,
        canViewLeaveRequests: true,
        canViewTeamAttendance: true,
        canViewPerformance: true,
        canViewWorkload: true
      });
      
      alert(`Manager added successfully!`);
    } catch (error) {
      console.error('Error adding manager:', error);
      console.error('Error response:', error.response?.data);
      alert(error.response?.data?.error || error.message || 'Failed to add manager');
    }
  };

  const handleRemoveManager = async (relationshipId) => {
    try {
      const token = localStorage.getItem('token');
      
      console.log('Removing manager relationship:', relationshipId);
      
      await axios.delete(`/api/managers/relationships/${relationshipId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Manager removed successfully');
      
      // Reload managers
      await loadUserManagers(user.id);
      
      alert('Manager removed successfully!');
    } catch (error) {
      console.error('Error removing manager:', error);
      console.error('Error response:', error.response?.data);
      alert(error.response?.data?.error || 'Failed to remove manager');
    }
  };

  const fetchTeamLeadSubordinates = async (userId) => {
    const token = localStorage.getItem('token');
    const response = await axios.get(`/api/managers/hierarchy/${userId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data?.allSubordinates || [];
  };

  const handleChange = async (field, value) => {
    if (field === 'isTeamLead' && value === false && formData.isTeamLead && user?.id) {
      setTeamLeadBlockLoading(true);
      setTeamLeadBlockError('');
      try {
        const subordinates = await fetchTeamLeadSubordinates(user.id);
        if (subordinates.length > 0) {
          setTeamLeadBlockSubordinates(subordinates);
          setTeamLeadBlockOpen(true);
          return;
        }
      } catch (error) {
        console.error('Failed to load subordinates:', error);
        setTeamLeadBlockError('Unable to verify assigned team members. Please try again.');
        setTeamLeadBlockSubordinates([]);
        setTeamLeadBlockOpen(true);
        return;
      } finally {
        setTeamLeadBlockLoading(false);
      }
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleAddressChange = (type, field, value) => {
    setFormData(prev => ({
      ...prev,
      addresses: {
        ...prev.addresses,
        [type]: {
          ...prev.addresses[type],
          [field]: value
        }
      }
    }));
  };

  const handleSameAsCurrent = (checked) => {
    setFormData(prev => ({
      ...prev,
      addresses: {
        ...prev.addresses,
        sameAsCurrent: checked,
        permanent: checked ? { ...prev.addresses.current, type: 'permanent' } : prev.addresses.permanent
      }
    }));
  };

  const handleIdentityChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      identity: {
        ...prev.identity,
        [field]: value
      }
    }));
  };

  const handleProfilePhotoUpload = (file) => {
    if (!file) return;
    setProfilePhotoUploadError('');
    setProfilePhotoUploading(true);
    const token = localStorage.getItem('token');

    const form = new FormData();
    form.append('file', file);
    form.append('folder', 'profile-photos');

    axios.post('/api/uploads/direct', form, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    })
      .then((response) => {
        const publicUrl = response.data?.publicUrl;
        if (!publicUrl) {
          throw new Error('No profile photo URL returned');
        }
        setFormData(prev => ({
          ...prev,
          profilePhotoUrl: publicUrl || ''
        }));
        setProfilePhotoFileName(file.name || '');
      })
      .catch(async (error) => {
        console.error('Profile photo upload failed:', error);
        setProfilePhotoUploadError('Upload failed. Please try again.');
      })
      .finally(() => {
        setProfilePhotoUploading(false);
      });
  };

  const updateListItem = (listKey, index, field, value) => {
    setFormData(prev => {
      const list = [...prev[listKey]];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, [listKey]: list };
    });
  };

  const addListItem = (listKey, emptyItem) => {
    setFormData(prev => ({
      ...prev,
      [listKey]: [...prev[listKey], { ...emptyItem }]
    }));
  };

  const removeListItem = (listKey, index) => {
    setFormData(prev => {
      const list = [...prev[listKey]];
      list.splice(index, 1);
      return { ...prev, [listKey]: list.length ? list : list };
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.officialEmail.trim()) {
      newErrors.officialEmail = 'Official email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.officialEmail)) {
      newErrors.officialEmail = 'Please enter a valid email address';
    }

    if (formData.alternateEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.alternateEmail)) {
      newErrors.alternateEmail = 'Please enter a valid email address';
    }

    if (formData.personalEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.personalEmail)) {
      newErrors.personalEmail = 'Please enter a valid personal email address';
    }

    if (formData.linkedinUrl && !/^https?:\/\/.+/i.test(formData.linkedinUrl)) {
      newErrors.linkedinUrl = 'Please enter a valid URL (starting with http/https)';
    }

    if (!formData.organizationId.trim()) {
      newErrors.organizationId = 'Organization ID is required';
    }

    if (formData.mobileNumber && !/^\d{10}$/.test(formData.mobileNumber.replace(/\D/g, ''))) {
      newErrors.mobileNumber = 'Please enter a valid 10-digit mobile number';
    }

    // Validate location fields
    // Only require both if at least one has a non-empty value
    const hasLatitude = formData.latitude && formData.latitude.trim().length > 0;
    const hasLongitude = formData.longitude && formData.longitude.trim().length > 0;
    
    if (hasLatitude || hasLongitude) {
      // If one is provided, both must be provided
      if (!hasLatitude || !hasLongitude) {
        newErrors.latitude = 'Both latitude and longitude must be provided together';
        newErrors.longitude = 'Both latitude and longitude must be provided together';
      } else {
        const lat = parseFloat(formData.latitude.trim());
        const lon = parseFloat(formData.longitude.trim());
        
        if (isNaN(lat) || lat < -90 || lat > 90) {
          newErrors.latitude = 'Latitude must be between -90 and 90';
        }
        
        if (isNaN(lon) || lon < -180 || lon > 180) {
          newErrors.longitude = 'Longitude must be between -180 and 180';
        }
      }
    }
    // If both are empty, that's valid - allows clearing the location

    if (formData.locationRadius) {
      const radius = parseInt(formData.locationRadius);
      if (isNaN(radius) || radius < 1) {
        newErrors.locationRadius = 'Radius must be a positive integer (minimum 1 meter)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      
      // Clean up form data before sending
      const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';
      const hasAddressData = (address) => Object.keys(address || {}).some(key => key !== 'type' && hasValue(address[key]));
      const hasIdentityData = (identity) => Object.values(identity || {}).some(v => hasValue(v));
      const cleanArray = (rows, validator) => rows.filter(validator);

      const addresses = [];
      if (hasAddressData(formData.addresses.current)) {
        addresses.push({ ...formData.addresses.current, type: 'current' });
      }
      const permanentAddress = formData.addresses.sameAsCurrent
        ? { ...formData.addresses.current, type: 'permanent' }
        : { ...formData.addresses.permanent, type: 'permanent' };
      if (hasAddressData(permanentAddress)) {
        addresses.push(permanentAddress);
      }

      const cleanFormData = {
        workingHourPolicyId: formData.workingHourPolicyId || null,
        Department: formData.Department || null,
        userStatus: formData.userStatus || 'Active',
        employementType: formData.employementType || 'Full-Time',
        isTeamLead: Boolean(formData.isTeamLead),
        isManager: Boolean(formData.isManager),
        ...(canEditAttendanceExclusion
          ? { excludeFromAttendanceStats: Boolean(formData.excludeFromAttendanceStats) }
          : {}),
        firstName: formData.firstName?.trim() || '',
        lastName: formData.lastName?.trim() || '',
        officialEmail: formData.officialEmail?.trim() || '',
        mobileNumber: formData.mobileNumber?.trim() || null,
        alternateEmail: formData.alternateEmail?.trim() || null,
        organizationId: formData.organizationId?.trim() || '',
        uidNumber: formData.uidNumber?.trim() || null,
        employeeCode: formData.employeeCode?.trim() || null,
        profilePhotoUrl: formData.profilePhotoUrl?.trim() || null,
        designation: formData.designation?.trim() || null,
        role: formData.role?.trim() || null,
        businessUnit: formData.businessUnit?.trim() || null,
        company: formData.company?.trim() || null,
        managerId: formData.managerId || null,
        orgChartReference: formData.orgChartReference?.trim() || null,
        dateOfJoining: formData.dateOfJoining || null,
        employeeSubType: formData.employeeSubType?.trim() || null,
        currentOfficeCountry: formData.currentOfficeCountry?.trim() || null,
        currentOfficeState: formData.currentOfficeState?.trim() || null,
        currentOfficeCity: formData.currentOfficeCity?.trim() || null,
        gender: formData.gender?.trim() || null,
        dateOfBirth: formData.dateOfBirth || null,
        nationality: formData.nationality?.trim() || null,
        fatherName: formData.fatherName?.trim() || null,
        bloodGroup: formData.bloodGroup?.trim() || null,
        languagesSpoken: formData.languagesSpoken
          ? formData.languagesSpoken.split(',').map(l => l.trim()).filter(Boolean)
          : null,
        personalEmail: formData.personalEmail?.trim() || null,
        personalMobileNumber: formData.personalMobileNumber?.trim() || null,
        officeMobileNumber: formData.officeMobileNumber?.trim() || null,
        linkedinUrl: formData.linkedinUrl?.trim() || null,
        mobileAccess: Boolean(formData.mobileAccess),
        latitude: formData.latitude?.trim() ? parseFloat(formData.latitude.trim()) : null,
        longitude: formData.longitude?.trim() ? parseFloat(formData.longitude.trim()) : null,
        locationRadius: formData.locationRadius?.trim() ? parseInt(formData.locationRadius.trim()) : null,
        addresses,
        emergencyContacts: cleanArray(formData.emergencyContacts || [], (row) => hasValue(row.name) || hasValue(row.phone) || hasValue(row.relation)),
        workHistory: cleanArray(formData.workHistory || [], (row) => hasValue(row.companyName) || hasValue(row.designation) || hasValue(row.fromDate) || hasValue(row.toDate) || hasValue(row.description)),
        education: cleanArray(formData.education || [], (row) => hasValue(row.degree) || hasValue(row.institution) || hasValue(row.fieldOfStudy) || hasValue(row.startYear) || hasValue(row.endYear)),
        roleHistory: cleanArray(formData.roleHistory || [], (row) => hasValue(row.designation) || hasValue(row.role) || hasValue(row.businessUnit) || hasValue(row.fromDate) || hasValue(row.toDate) || hasValue(row.notes)),
        managerHistory: cleanArray(formData.managerHistory || [], (row) => hasValue(row.managerId) || hasValue(row.fromDate) || hasValue(row.toDate)),
        documents: cleanArray(formData.documents || [], (row) => hasValue(row.type) || hasValue(row.fileUrl)),
        identity: hasIdentityData(formData.identity) ? {
          ...formData.identity,
          aadhaarLast4: formData.identity.aadhaarLast4?.trim() || null,
          panNumber: formData.identity.panNumber?.trim() || null,
          passportNumber: formData.identity.passportNumber?.trim() || null,
          epfNumber: formData.identity.epfNumber?.trim() || null,
          uanNumber: formData.identity.uanNumber?.trim() || null,
          pranNumber: formData.identity.pranNumber?.trim() || null
        } : null
      };
      
      await onSave(cleanFormData);
    } catch (err) {
      const errorCode = err?.code || err?.response?.data?.code;
      const subordinateList = err?.subordinates || err?.response?.data?.subordinates || [];

      if (errorCode === 'TEAM_LEAD_HAS_SUBORDINATES') {
        setFormData(prev => ({ ...prev, isTeamLead: true }));
        setTeamLeadBlockSubordinates(subordinateList);
        setTeamLeadBlockError('This user has assigned team members. Remove or reassign them before unchecking Team Lead.');
        setTeamLeadBlockOpen(true);
        return;
      }

      const message = err?.message || 'Failed to update user';
      const fieldErrors = {};
      const fieldTabMap = {
        firstName: 'overview',
        lastName: 'overview',
        officialEmail: 'overview',
        alternateEmail: 'overview',
        mobileNumber: 'overview',
        organizationId: 'overview',
        uidNumber: 'overview',
        employeeCode: 'overview',
        designation: 'overview',
        role: 'overview',
        businessUnit: 'overview',
        company: 'overview',
        managerId: 'overview',
        orgChartReference: 'overview',
        userStatus: 'overview',
        employementType: 'overview',
        workingHourPolicyId: 'overview',
        gender: 'personal',
        dateOfBirth: 'personal',
        nationality: 'personal',
        fatherName: 'personal',
        bloodGroup: 'personal',
        languagesSpoken: 'personal',
        personalEmail: 'contact',
        personalMobileNumber: 'contact',
        officeMobileNumber: 'contact',
        linkedinUrl: 'contact',
        dateOfJoining: 'employment',
        employeeSubType: 'employment',
        currentOfficeCountry: 'employment',
        currentOfficeState: 'employment',
        currentOfficeCity: 'employment',
        latitude: 'employment',
        longitude: 'employment',
        locationRadius: 'employment'
      };

      if (Array.isArray(err?.fields)) {
        err.fields.forEach((field) => {
          if (field?.field) {
            fieldErrors[field.field] = field.message || 'Invalid value';
          }
        });
      }

      setErrors({ submit: message, ...fieldErrors });

      const firstField = Object.keys(fieldErrors)[0];
      if (firstField) {
        const targetTab = fieldTabMap[firstField] || 'overview';
        setActiveTab(targetTab);
        setTimeout(() => {
          const el = document.querySelector(`[data-field="${firstField}"]`);
          if (el && typeof el.focus === 'function') {
            el.focus();
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 50);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-60 backdrop-blur-sm transition-opacity" onClick={onClose} />

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full border-2 border-gray-200">
          {/* Modal Header - Gradient */}
          <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-6 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold text-white drop-shadow-md">
                  {mode === 'create' ? '✨ Add New User' : '✏️ Edit User'}
                </h3>
                <p className="text-white/80 text-sm mt-1">
                  {mode === 'create' ? 'Create a new employee profile' : 'Update employee information'}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white hover:bg-white/20 rounded-lg p-2 transition-all duration-200"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
          
          <div className="bg-white px-6 pt-6 pb-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="text-red-800 text-sm">{errors.submit}</div>
                </div>
              )}

              {/* Tabs */}
              <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-3">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      activeTab === tab.id
                        ? 'bg-indigo-600 text-white shadow'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {loadingUserDetails && (
                <div className="flex items-center justify-center py-6">
                  <div className="animate-spin h-7 w-7 border-4 border-indigo-200 border-t-indigo-600 rounded-full"></div>
                  <span className="ml-3 text-sm text-gray-600">Loading profile...</span>
                </div>
              )}

              {!loadingUserDetails && activeTab === 'overview' && (
                <>
                  {/* Name Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        First Name <span className="text-red-500">*</span>
                      </label>
                    <input
                      type="text"
                      data-field="firstName"
                      value={formData.firstName}
                        onChange={(e) => handleChange('firstName', e.target.value)}
                        className={`block w-full border-2 rounded-lg shadow-sm px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
                          errors.firstName ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                        }`}
                        placeholder="Enter first name"
                      />
                      {errors.firstName && (
                        <p className="mt-1.5 text-sm text-red-600 font-medium">{errors.firstName}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                    <input
                      type="text"
                      data-field="lastName"
                      value={formData.lastName}
                        onChange={(e) => handleChange('lastName', e.target.value)}
                        className={`block w-full border-2 rounded-lg shadow-sm px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
                          errors.lastName ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                        }`}
                        placeholder="Enter last name"
                      />
                      {errors.lastName && (
                        <p className="mt-1.5 text-sm text-red-600 font-medium">{errors.lastName}</p>
                      )}
                    </div>
                  </div>

                  {/* Employee + Profile */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        Employee ID
                      </label>
                      <input
                        type="text"
                        data-field="employeeCode"
                        value={formData.employeeCode}
                        onChange={(e) => handleChange('employeeCode', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        placeholder="EMP-0001"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        Profile Photo
                      </label>
                      <div className="flex gap-2 mb-2">
                        <button
                          type="button"
                          onClick={() => setProfilePhotoMode('url')}
                          className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${
                            profilePhotoMode === 'url'
                              ? 'bg-indigo-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          Use Link
                        </button>
                        <button
                          type="button"
                          onClick={() => setProfilePhotoMode('upload')}
                          className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${
                            profilePhotoMode === 'upload'
                              ? 'bg-indigo-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          Upload File
                        </button>
                      </div>

                      {profilePhotoMode === 'url' && (
                        <input
                          type="text"
                          data-field="profilePhotoUrl"
                          value={formData.profilePhotoUrl}
                          onChange={(e) => handleChange('profilePhotoUrl', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                          placeholder="https://..."
                        />
                      )}

                      {profilePhotoMode === 'upload' && (
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-sm text-gray-600">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleProfilePhotoUpload(e.target.files?.[0])}
                            disabled={profilePhotoUploading}
                          />
                          {profilePhotoFileName && (
                            <div className="mt-2 text-xs text-gray-500">
                              Selected: {profilePhotoFileName}
                            </div>
                          )}
                          {profilePhotoUploading && (
                            <div className="mt-2 text-xs text-indigo-600">Uploading...</div>
                          )}
                          {profilePhotoUploadError && (
                            <div className="mt-2 text-xs text-red-600">{profilePhotoUploadError}</div>
                          )}
                          <div className="mt-2 text-xs text-gray-500">
                            Uploads directly to S3.
                          </div>
                        </div>
                      )}

                      {formData.profilePhotoUrl && (
                        <div className="mt-3 flex items-center gap-3">
                          <div className="h-12 w-12 rounded-full overflow-hidden border border-gray-200">
                            <img
                              src={formData.profilePhotoUrl}
                              alt="Profile preview"
                              className="h-full w-full object-cover"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => handleChange('profilePhotoUrl', '')}
                            className="text-xs text-red-600 hover:text-red-700"
                          >
                            Remove photo
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Email Fields */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      Official Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      data-field="officialEmail"
                      value={formData.officialEmail}
                      onChange={(e) => handleChange('officialEmail', e.target.value)}
                      className={`block w-full border-2 rounded-lg shadow-sm px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
                        errors.officialEmail ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                      }`}
                      placeholder="employee@company.com"
                    />
                    {errors.officialEmail && (
                      <p className="mt-1.5 text-sm text-red-600 font-medium">{errors.officialEmail}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Alternate Email
                    </label>
                    <input
                      type="email"
                      data-field="alternateEmail"
                      value={formData.alternateEmail}
                      onChange={(e) => handleChange('alternateEmail', e.target.value)}
                      className={`mt-1 block w-full border rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                        errors.alternateEmail ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.alternateEmail && (
                      <p className="mt-1 text-sm text-red-600">{errors.alternateEmail}</p>
                    )}
                  </div>

                  {/* Contact and Organization */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        Mobile Number
                      </label>
                    <input
                      type="tel"
                      data-field="mobileNumber"
                      value={formData.mobileNumber}
                        onChange={(e) => handleChange('mobileNumber', e.target.value)}
                        className={`mt-1 block w-full border rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                          errors.mobileNumber ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {errors.mobileNumber && (
                        <p className="mt-1 text-sm text-red-600">{errors.mobileNumber}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        Organization ID *
                      </label>
                    <input
                      type="text"
                      data-field="organizationId"
                      value={formData.organizationId}
                        onChange={(e) => handleChange('organizationId', e.target.value)}
                        className={`mt-1 block w-full border rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                          errors.organizationId ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {errors.organizationId && (
                        <p className="mt-1 text-sm text-red-600">{errors.organizationId}</p>
                      )}
                    </div>
                  </div>

                  {/* UID and Department */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        UID Number
                      </label>
                    <input
                      type="text"
                      data-field="uidNumber"
                      value={formData.uidNumber}
                        onChange={(e) => handleChange('uidNumber', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        Department
                      </label>
                    <select
                      data-field="Department"
                      value={formData.Department}
                        onChange={(e) => handleChange('Department', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      >
                        <option value="">Select Department</option>
                        {departments.map(dept => (
                          <option key={dept} value={dept}>{dept}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Status and Employment Type */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        Status
                      </label>
                    <select
                      data-field="userStatus"
                      value={formData.userStatus}
                        onChange={(e) => handleChange('userStatus', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      >
                        <option value="Active">Active</option>
                        <option value="Not Active">Not Active</option>
                        <option value="Restricted">Restricted</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        Employment Type
                      </label>
                    <select
                      data-field="employementType"
                      value={formData.employementType}
                        onChange={(e) => handleChange('employementType', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      >
                        <option value="Full-Time">Full-Time</option>
                        <option value="Intern">Intern</option>
                        <option value="Contract">Contract</option>
                        <option value="Part-Time">Part-Time</option>
                        <option value="Permanent">Permanent</option>
                      </select>
                    </div>
                  </div>

                  {/* Working Hours Policy */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      Working Hours Policy
                    </label>
                    <select
                      data-field="workingHourPolicyId"
                      value={formData.workingHourPolicyId}
                      onChange={(e) => handleChange('workingHourPolicyId', e.target.value)}
                      className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    >
                      <option value="">Select Policy</option>
                      {workingPolicies.map(policy => (
                        <option key={policy.id} value={policy.id}>
                          {policy.policyName} ({policy.requiredDailyHours}h)
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        Designation
                      </label>
                    <input
                      type="text"
                      data-field="designation"
                      value={formData.designation}
                        onChange={(e) => handleChange('designation', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        placeholder="e.g., Senior Analyst"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        Role
                      </label>
                    <input
                      type="text"
                      data-field="role"
                      value={formData.role}
                        onChange={(e) => handleChange('role', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        placeholder="e.g., Operations"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        Business Unit
                      </label>
                    <input
                      type="text"
                      data-field="businessUnit"
                      value={formData.businessUnit}
                        onChange={(e) => handleChange('businessUnit', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        placeholder="e.g., EMS"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        Company / Group
                      </label>
                    <input
                      type="text"
                      data-field="company"
                      value={formData.company}
                        onChange={(e) => handleChange('company', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        placeholder="Group company"
                      />
                    </div>
                  </div>


                  {/* Team Lead Checkbox */}
                  <div className="flex items-center">
                    <input
                      id="isTeamLead"
                      type="checkbox"
                      checked={formData.isTeamLead}
                      onChange={(e) => handleChange('isTeamLead', e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isTeamLead" className="ml-2 block text-sm text-gray-900">
                      Is Team Lead
                    </label>
                  </div>

                  {/* Manager Checkbox */}
                  <div className="flex items-center">
                    <input
                      id="isManager"
                      type="checkbox"
                      checked={formData.isManager}
                      onChange={(e) => handleChange('isManager', e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isManager" className="ml-2 block text-sm text-gray-900">
                      Is Manager
                    </label>
                  </div>

                  {canEditAttendanceExclusion && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-3 space-y-1">
                      <div className="flex items-start gap-2">
                        <input
                          id="excludeFromAttendanceStats"
                          type="checkbox"
                          checked={formData.excludeFromAttendanceStats}
                          onChange={(e) => handleChange('excludeFromAttendanceStats', e.target.checked)}
                          className="h-4 w-4 mt-0.5 text-amber-700 focus:ring-amber-500 border-gray-300 rounded"
                        />
                        <div>
                          <label htmlFor="excludeFromAttendanceStats" className="block text-sm font-medium text-gray-900">
                            Exclude from attendance stats
                          </label>
                          <p className="text-xs text-gray-600 mt-0.5">
                            Omit this person from team summary emails, manager team attendance, and dashboard attendance counts. Their own check-ins and records are unchanged.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {!loadingUserDetails && activeTab === 'personal' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Gender</label>
                    <select
                      data-field="gender"
                      value={formData.gender}
                        onChange={(e) => handleChange('gender', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      >
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Date of Birth</label>
                    <input
                      type="date"
                      data-field="dateOfBirth"
                      value={formData.dateOfBirth}
                        onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Nationality</label>
                    <input
                      type="text"
                      data-field="nationality"
                      value={formData.nationality}
                        onChange={(e) => handleChange('nationality', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Father’s Name</label>
                    <input
                      type="text"
                      data-field="fatherName"
                      value={formData.fatherName}
                        onChange={(e) => handleChange('fatherName', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Blood Group</label>
                    <input
                      type="text"
                      data-field="bloodGroup"
                      value={formData.bloodGroup}
                        onChange={(e) => handleChange('bloodGroup', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        placeholder="e.g., B+"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Languages Spoken</label>
                    <input
                      type="text"
                      data-field="languagesSpoken"
                      value={formData.languagesSpoken}
                        onChange={(e) => handleChange('languagesSpoken', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        placeholder="English, Hindi"
                      />
                    </div>
                  </div>
                </>
              )}

              {!loadingUserDetails && activeTab === 'contact' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Personal Email</label>
                    <input
                      type="email"
                      data-field="personalEmail"
                      value={formData.personalEmail}
                        onChange={(e) => handleChange('personalEmail', e.target.value)}
                        className={`block w-full border-2 rounded-lg shadow-sm px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
                          errors.personalEmail ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                        }`}
                      />
                      {errors.personalEmail && (
                        <p className="mt-1.5 text-sm text-red-600 font-medium">{errors.personalEmail}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Personal Mobile</label>
                    <input
                      type="tel"
                      data-field="personalMobileNumber"
                      value={formData.personalMobileNumber}
                        onChange={(e) => handleChange('personalMobileNumber', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Office Mobile</label>
                    <input
                      type="tel"
                      data-field="officeMobileNumber"
                      value={formData.officeMobileNumber}
                        onChange={(e) => handleChange('officeMobileNumber', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">LinkedIn Profile</label>
                    <input
                      type="text"
                      data-field="linkedinUrl"
                      value={formData.linkedinUrl}
                        onChange={(e) => handleChange('linkedinUrl', e.target.value)}
                        className={`block w-full border-2 rounded-lg shadow-sm px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
                          errors.linkedinUrl ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                        }`}
                        placeholder="https://linkedin.com/in/..."
                      />
                      {errors.linkedinUrl && (
                        <p className="mt-1.5 text-sm text-red-600 font-medium">{errors.linkedinUrl}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      id="mobileAccess"
                      type="checkbox"
                      checked={formData.mobileAccess}
                      onChange={(e) => handleChange('mobileAccess', e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="mobileAccess" className="ml-2 block text-sm text-gray-900">
                      Mobile Access Enabled
                    </label>
                  </div>
                </>
              )}

              {!loadingUserDetails && activeTab === 'address' && (
                <>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                    <h4 className="text-base font-bold text-gray-900 mb-3">Current Address</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="text"
                        value={formData.addresses.current.houseNo}
                        onChange={(e) => handleAddressChange('current', 'houseNo', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Flat / House No"
                      />
                      <input
                        type="text"
                        value={formData.addresses.current.street}
                        onChange={(e) => handleAddressChange('current', 'street', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Street / Area"
                      />
                      <input
                        type="text"
                        value={formData.addresses.current.landmark}
                        onChange={(e) => handleAddressChange('current', 'landmark', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Landmark"
                      />
                      <input
                        type="text"
                        value={formData.addresses.current.city}
                        onChange={(e) => handleAddressChange('current', 'city', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="City"
                      />
                      <input
                        type="text"
                        value={formData.addresses.current.state}
                        onChange={(e) => handleAddressChange('current', 'state', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="State"
                      />
                      <input
                        type="text"
                        value={formData.addresses.current.country}
                        onChange={(e) => handleAddressChange('current', 'country', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Country"
                      />
                      <input
                        type="text"
                        value={formData.addresses.current.pincode}
                        onChange={(e) => handleAddressChange('current', 'pincode', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Pincode"
                      />
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      id="sameAsCurrent"
                      type="checkbox"
                      checked={formData.addresses.sameAsCurrent}
                      onChange={(e) => handleSameAsCurrent(e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="sameAsCurrent" className="ml-2 block text-sm text-gray-900">
                      Permanent address same as current
                    </label>
                  </div>

                  {!formData.addresses.sameAsCurrent && (
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                      <h4 className="text-base font-bold text-gray-900 mb-3">Permanent Address</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <input
                          type="text"
                          value={formData.addresses.permanent.houseNo}
                          onChange={(e) => handleAddressChange('permanent', 'houseNo', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Flat / House No"
                        />
                        <input
                          type="text"
                          value={formData.addresses.permanent.street}
                          onChange={(e) => handleAddressChange('permanent', 'street', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Street / Area"
                        />
                        <input
                          type="text"
                          value={formData.addresses.permanent.landmark}
                          onChange={(e) => handleAddressChange('permanent', 'landmark', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Landmark"
                        />
                        <input
                          type="text"
                          value={formData.addresses.permanent.city}
                          onChange={(e) => handleAddressChange('permanent', 'city', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="City"
                        />
                        <input
                          type="text"
                          value={formData.addresses.permanent.state}
                          onChange={(e) => handleAddressChange('permanent', 'state', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="State"
                        />
                        <input
                          type="text"
                          value={formData.addresses.permanent.country}
                          onChange={(e) => handleAddressChange('permanent', 'country', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Country"
                        />
                        <input
                          type="text"
                          value={formData.addresses.permanent.pincode}
                          onChange={(e) => handleAddressChange('permanent', 'pincode', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Pincode"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              {!loadingUserDetails && activeTab === 'employment' && (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Date of Joining</label>
                    <input
                      type="date"
                      data-field="dateOfJoining"
                      value={formData.dateOfJoining}
                        onChange={(e) => handleChange('dateOfJoining', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Employee Sub-Type</label>
                    <input
                      type="text"
                      data-field="employeeSubType"
                      value={formData.employeeSubType}
                        onChange={(e) => handleChange('employeeSubType', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        placeholder="e.g., Probation"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Office City</label>
                    <input
                      type="text"
                      data-field="currentOfficeCity"
                      value={formData.currentOfficeCity}
                        onChange={(e) => handleChange('currentOfficeCity', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Office State</label>
                    <input
                      type="text"
                      data-field="currentOfficeState"
                      value={formData.currentOfficeState}
                        onChange={(e) => handleChange('currentOfficeState', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Office Country</label>
                    <input
                      type="text"
                      data-field="currentOfficeCountry"
                      value={formData.currentOfficeCountry}
                        onChange={(e) => handleChange('currentOfficeCountry', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      />
                    </div>
                  </div>

                  {canEditAttendanceExclusion && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-3 space-y-1">
                      <div className="flex items-start gap-2">
                        <input
                          id="excludeFromAttendanceStats"
                          type="checkbox"
                          checked={formData.excludeFromAttendanceStats}
                          onChange={(e) => handleChange('excludeFromAttendanceStats', e.target.checked)}
                          className="h-4 w-4 mt-0.5 text-amber-700 focus:ring-amber-500 border-gray-300 rounded"
                        />
                        <div>
                          <label htmlFor="excludeFromAttendanceStats" className="block text-sm font-medium text-gray-900">
                            Exclude from attendance stats
                          </label>
                          <p className="text-xs text-gray-600 mt-0.5">
                            Omit this person from team summary emails, manager team attendance, and dashboard attendance counts. Their own check-ins and records are unchanged.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Role History */}
                  <div className="mt-2">
                    <h4 className="text-base font-bold text-gray-900 mb-3">Work Role History</h4>
                    {formData.roleHistory.map((row, index) => (
                      <div key={`role-${index}`} className="border border-gray-200 rounded-xl p-4 space-y-3 mb-3">
                        <div className="grid grid-cols-3 gap-4">
                          <input
                            type="text"
                            value={row.designation}
                            onChange={(e) => updateListItem('roleHistory', index, 'designation', e.target.value)}
                            className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Designation"
                          />
                          <input
                            type="text"
                            value={row.role}
                            onChange={(e) => updateListItem('roleHistory', index, 'role', e.target.value)}
                            className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Role"
                          />
                          <input
                            type="text"
                            value={row.businessUnit}
                            onChange={(e) => updateListItem('roleHistory', index, 'businessUnit', e.target.value)}
                            className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Business Unit"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <input
                            type="date"
                            value={row.fromDate || ''}
                            onChange={(e) => updateListItem('roleHistory', index, 'fromDate', e.target.value)}
                            className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <input
                            type="date"
                            value={row.toDate || ''}
                            onChange={(e) => updateListItem('roleHistory', index, 'toDate', e.target.value)}
                            className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <textarea
                          value={row.notes || ''}
                          onChange={(e) => updateListItem('roleHistory', index, 'notes', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          rows={2}
                          placeholder="Notes"
                        />
                        {formData.roleHistory.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeListItem('roleHistory', index)}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addListItem('roleHistory', emptyRoleHistory)}
                      className="px-4 py-2 rounded-lg bg-indigo-50 text-indigo-600 font-semibold text-sm hover:bg-indigo-100"
                    >
                      + Add Role History
                    </button>
                  </div>

                  {/* Manager History */}
                  <div className="mt-4">
                    <h4 className="text-base font-bold text-gray-900 mb-3">Manager History</h4>
                    {formData.managerHistory.map((row, index) => (
                      <div key={`manager-${index}`} className="border border-gray-200 rounded-xl p-4 space-y-3 mb-3">
                        <div className="grid grid-cols-2 gap-4">
                          <select
                            value={row.managerId || ''}
                            onChange={(e) => updateListItem('managerHistory', index, 'managerId', e.target.value)}
                            className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Select Manager</option>
                            {users
                              .filter(u => u.id !== user?.id)
                              .map(manager => {
                                const name = `${manager.firstName || ''} ${manager.lastName || ''}`.trim() || manager.officialEmail;
                                return (
                                  <option key={manager.id} value={manager.id}>
                                    {name} ({manager.officialEmail})
                                  </option>
                                );
                              })}
                          </select>
                          <input
                            type="date"
                            value={row.fromDate || ''}
                            onChange={(e) => updateListItem('managerHistory', index, 'fromDate', e.target.value)}
                            className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <input
                            type="date"
                            value={row.toDate || ''}
                            onChange={(e) => updateListItem('managerHistory', index, 'toDate', e.target.value)}
                            className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        {formData.managerHistory.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeListItem('managerHistory', index)}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addListItem('managerHistory', emptyManagerHistory)}
                      className="px-4 py-2 rounded-lg bg-indigo-50 text-indigo-600 font-semibold text-sm hover:bg-indigo-100"
                    >
                      + Add Manager History
                    </button>
                  </div>
                </>
              )}

              {!loadingUserDetails && activeTab === 'experience' && (
                <>
                  {formData.workHistory.map((row, index) => (
                    <div key={`work-${index}`} className="border border-gray-200 rounded-xl p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <input
                          type="text"
                          value={row.companyName}
                          onChange={(e) => updateListItem('workHistory', index, 'companyName', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Company Name"
                        />
                        <input
                          type="text"
                          value={row.designation}
                          onChange={(e) => updateListItem('workHistory', index, 'designation', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Designation"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <input
                          type="date"
                          value={row.fromDate || ''}
                          onChange={(e) => updateListItem('workHistory', index, 'fromDate', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <input
                          type="date"
                          value={row.toDate || ''}
                          onChange={(e) => updateListItem('workHistory', index, 'toDate', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <textarea
                        value={row.description}
                        onChange={(e) => updateListItem('workHistory', index, 'description', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows={2}
                        placeholder="Description"
                      />
                      {formData.workHistory.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeListItem('workHistory', index)}
                          className="text-sm text-red-600 hover:text-red-700"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addListItem('workHistory', emptyWorkHistory)}
                    className="px-4 py-2 rounded-lg bg-indigo-50 text-indigo-600 font-semibold text-sm hover:bg-indigo-100"
                  >
                    + Add Experience
                  </button>
                </>
              )}

              {!loadingUserDetails && activeTab === 'education' && (
                <>
                  {formData.education.map((row, index) => (
                    <div key={`edu-${index}`} className="border border-gray-200 rounded-xl p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <input
                          type="text"
                          value={row.degree}
                          onChange={(e) => updateListItem('education', index, 'degree', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Degree"
                        />
                        <input
                          type="text"
                          value={row.institution}
                          onChange={(e) => updateListItem('education', index, 'institution', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Institution"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <input
                          type="text"
                          value={row.fieldOfStudy}
                          onChange={(e) => updateListItem('education', index, 'fieldOfStudy', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Field of Study"
                        />
                        <input
                          type="number"
                          value={row.startYear || ''}
                          onChange={(e) => updateListItem('education', index, 'startYear', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Start Year"
                        />
                        <input
                          type="number"
                          value={row.endYear || ''}
                          onChange={(e) => updateListItem('education', index, 'endYear', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="End Year"
                        />
                      </div>
                      {formData.education.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeListItem('education', index)}
                          className="text-sm text-red-600 hover:text-red-700"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addListItem('education', emptyEducation)}
                    className="px-4 py-2 rounded-lg bg-indigo-50 text-indigo-600 font-semibold text-sm hover:bg-indigo-100"
                  >
                    + Add Education
                  </button>
                </>
              )}

              {!loadingUserDetails && activeTab === 'identity' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Aadhaar Last 4</label>
                      <input
                        type="text"
                        value={formData.identity.aadhaarLast4}
                        onChange={(e) => handleIdentityChange('aadhaarLast4', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        placeholder="1234"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">PAN Number</label>
                      <input
                        type="text"
                        value={formData.identity.panNumber}
                        onChange={(e) => handleIdentityChange('panNumber', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Passport Number</label>
                      <input
                        type="text"
                        value={formData.identity.passportNumber}
                        onChange={(e) => handleIdentityChange('passportNumber', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">EPF Number</label>
                      <input
                        type="text"
                        value={formData.identity.epfNumber}
                        onChange={(e) => handleIdentityChange('epfNumber', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">UAN Number</label>
                      <input
                        type="text"
                        value={formData.identity.uanNumber}
                        onChange={(e) => handleIdentityChange('uanNumber', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">PRAN Number</label>
                      <input
                        type="text"
                        value={formData.identity.pranNumber}
                        onChange={(e) => handleIdentityChange('pranNumber', e.target.value)}
                        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      />
                    </div>
                  </div>
                </>
              )}

              {!loadingUserDetails && activeTab === 'documents' && (
                <>
                  {formData.documents.map((row, index) => (
                    <div key={`doc-${index}`} className="border border-gray-200 rounded-xl p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <select
                          value={row.type}
                          onChange={(e) => updateListItem('documents', index, 'type', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="aadhaar_front">Aadhaar Front</option>
                          <option value="aadhaar_back">Aadhaar Back</option>
                          <option value="pan_card">PAN Card</option>
                          <option value="passport">Passport</option>
                          <option value="driving_license">Driving License</option>
                          <option value="bank_passbook">Bank Passbook</option>
                          <option value="signature">Signature</option>
                        </select>
                        <input
                          type="text"
                          value={row.fileUrl}
                          onChange={(e) => updateListItem('documents', index, 'fileUrl', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="File URL"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <input
                          type="text"
                          value={row.mimeType || ''}
                          onChange={(e) => updateListItem('documents', index, 'mimeType', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="MIME type"
                        />
                        <input
                          type="number"
                          value={row.fileSize || ''}
                          onChange={(e) => updateListItem('documents', index, 'fileSize', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="File size (bytes)"
                        />
                      </div>
                      {formData.documents.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeListItem('documents', index)}
                          className="text-sm text-red-600 hover:text-red-700"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addListItem('documents', emptyDocument)}
                    className="px-4 py-2 rounded-lg bg-indigo-50 text-indigo-600 font-semibold text-sm hover:bg-indigo-100"
                  >
                    + Add Document
                  </button>
                </>
              )}

              {!loadingUserDetails && activeTab === 'emergency' && (
                <>
                  {formData.emergencyContacts.map((row, index) => (
                    <div key={`em-${index}`} className="border border-gray-200 rounded-xl p-4 space-y-3">
                      <div className="grid grid-cols-3 gap-4">
                        <input
                          type="text"
                          value={row.name}
                          onChange={(e) => updateListItem('emergencyContacts', index, 'name', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Contact Name"
                        />
                        <input
                          type="tel"
                          value={row.phone}
                          onChange={(e) => updateListItem('emergencyContacts', index, 'phone', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Mobile"
                        />
                        <input
                          type="text"
                          value={row.relation}
                          onChange={(e) => updateListItem('emergencyContacts', index, 'relation', e.target.value)}
                          className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Relation"
                        />
                      </div>
                      {formData.emergencyContacts.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeListItem('emergencyContacts', index)}
                          className="text-sm text-red-600 hover:text-red-700"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addListItem('emergencyContacts', { name: '', phone: '', relation: '' })}
                    className="px-4 py-2 rounded-lg bg-indigo-50 text-indigo-600 font-semibold text-sm hover:bg-indigo-100"
                  >
                    + Add Emergency Contact
                  </button>
                </>
              )}

              {!loadingUserDetails && activeTab === 'employment' && (
                <>
                  {/* Location Settings Section */}
                  <div className="mt-6 pt-6 border-t-2 border-gray-200">
                <h4 className="text-lg font-bold text-gray-900 mb-4">Location Settings</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Set a specific check-in location for this user. If set, check-ins will be validated against this location instead of department locations.
                </p>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      Latitude
                    </label>
                    <input
                      type="number"
                      step="any"
                      data-field="latitude"
                      value={formData.latitude}
                      onChange={(e) => handleChange('latitude', e.target.value)}
                      className={`block w-full border-2 rounded-lg shadow-sm px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
                        errors.latitude ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                      }`}
                      placeholder="e.g., 28.6139"
                    />
                    {errors.latitude && (
                      <p className="mt-1.5 text-sm text-red-600 font-medium">{errors.latitude}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      Longitude
                    </label>
                    <input
                      type="number"
                      step="any"
                      data-field="longitude"
                      value={formData.longitude}
                      onChange={(e) => handleChange('longitude', e.target.value)}
                      className={`block w-full border-2 rounded-lg shadow-sm px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
                        errors.longitude ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                      }`}
                      placeholder="e.g., 77.2090"
                    />
                    {errors.longitude && (
                      <p className="mt-1.5 text-sm text-red-600 font-medium">{errors.longitude}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    Location Radius (meters)
                  </label>
                    <input
                      type="number"
                      min="1"
                      data-field="locationRadius"
                      value={formData.locationRadius}
                    onChange={(e) => handleChange('locationRadius', e.target.value)}
                    className={`block w-full border-2 rounded-lg shadow-sm px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
                      errors.locationRadius ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                    }`}
                    placeholder="Default: 100"
                  />
                  {errors.locationRadius && (
                    <p className="mt-1.5 text-sm text-red-600 font-medium">{errors.locationRadius}</p>
                  )}
                  <p className="mt-1.5 text-xs text-gray-500">
                    Allowed radius in meters for check-in validation (default: 100m)
                  </p>
                </div>
              </div>

                  {/* Multiple Managers Section - Only in Edit Mode */}
                  {mode === 'edit' && user?.id && (
                    <div className="mt-6 pt-6 border-t-2 border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-lg font-bold text-gray-900">Additional Reporting Managers</h4>
                      <p className="text-sm text-gray-600">Assign multiple managers who can view this employee's data</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowManagerSection(!showManagerSection)}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-semibold"
                    >
                      {showManagerSection ? 'Hide' : 'Manage Managers'}
                    </button>
                  </div>

                  {showManagerSection && (
                    <div className="space-y-4">
                      {/* Current Managers List */}
                      {loadingManagers ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin h-8 w-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full"></div>
                        </div>
                      ) : managers.length > 0 ? (
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Current Managers ({managers.length})
                          </label>
                          {managers.map((mgr) => (
                            <div key={mgr.id} className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                  <div className="h-10 w-10 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold">
                                    {mgr.managerName.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                  </div>
                                  <div>
                                    <div className="text-sm font-bold text-gray-900">{mgr.managerName}</div>
                                    <div className="text-xs text-gray-600">{mgr.managerEmail}</div>
                                  </div>
                                  {mgr.isPrimary && (
                                    <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs font-bold rounded-full">
                                      Primary
                                    </span>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveManager(mgr.id)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Remove manager"
                                >
                                  <XMarkIcon className="h-5 w-5" />
                                </button>
                              </div>
                              
                              {/* Permission Badges */}
                              {mgr.permissions && (
                                <div className="flex flex-wrap gap-1.5 pl-13">
                                  {mgr.permissions.canViewCheckoutRequests && (
                                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                                      Checkout
                                    </span>
                                  )}
                                  {mgr.permissions.canViewLeaveRequests && (
                                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                                      Leave
                                    </span>
                                  )}
                                  {mgr.permissions.canViewTeamAttendance && (
                                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                                      Attendance
                                    </span>
                                  )}
                                  {mgr.permissions.canViewPerformance && (
                                    <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded">
                                      Performance
                                    </span>
                                  )}
                                  {mgr.permissions.canViewWorkload && (
                                    <span className="px-2 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded">
                                      Workload
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6 text-gray-500">
                          <p className="text-sm">No additional managers assigned</p>
                        </div>
                      )}

                      {/* Add Manager */}
                      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl p-4">
                        <label className="block text-sm font-bold text-gray-900 mb-3">
                          Add New Manager
                        </label>
                        
                        {/* Manager Selection */}
                        <Select
                          isClearable
                          placeholder="Search and select manager to add..."
                          value={null}
                          onChange={(option) => {
                            if (option) handleAddManager(option.value);
                          }}
                          options={users
                            .filter(u => u.id !== user?.id && !managers.some(m => m.managerId === u.id))
                            .map(manager => {
                              const fullName = `${manager.firstName || ''} ${manager.lastName || ''}`.trim() || manager.officialEmail;
                              return {
                                value: manager.id,
                                label: `${fullName} (${manager.officialEmail})`,
                                email: manager.officialEmail
                              };
                            })}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              minHeight: 38,
                              borderWidth: 2,
                              borderColor: state.isFocused ? '#4f46e5' : '#d1d5db',
                              boxShadow: state.isFocused ? '0 0 0 1px #4f46e5' : 'none',
                              '&:hover': { borderColor: '#9ca3af' }
                            }),
                            option: (base, state) => ({
                              ...base,
                              fontSize: 14,
                              backgroundColor: state.isFocused ? '#eef2ff' : 'white',
                              color: '#111827'
                            }),
                            menuPortal: base => ({ ...base, zIndex: 9999 })
                          }}
                          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                          menuPlacement="auto"
                          menuPosition="fixed"
                        />
                        
                        {/* Permissions Checkboxes */}
                        <div className="mt-4 p-3 bg-white rounded-lg border border-indigo-200">
                          <label className="block text-xs font-bold text-gray-700 mb-3 uppercase">
                            Manager Permissions
                          </label>
                          <div className="grid grid-cols-2 gap-3">
                            <label className="flex items-center gap-2 cursor-pointer hover:bg-indigo-50 p-2 rounded transition-colors">
                              <input
                                type="checkbox"
                                checked={newManagerPermissions.canViewCheckoutRequests}
                                onChange={(e) => setNewManagerPermissions({
                                  ...newManagerPermissions,
                                  canViewCheckoutRequests: e.target.checked
                                })}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              />
                              <span className="text-sm text-gray-700">Checkout Requests</span>
                            </label>

                            <label className="flex items-center gap-2 cursor-pointer hover:bg-indigo-50 p-2 rounded transition-colors">
                              <input
                                type="checkbox"
                                checked={newManagerPermissions.canViewLeaveRequests}
                                onChange={(e) => setNewManagerPermissions({
                                  ...newManagerPermissions,
                                  canViewLeaveRequests: e.target.checked
                                })}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              />
                              <span className="text-sm text-gray-700">Leave Requests</span>
                            </label>

                            <label className="flex items-center gap-2 cursor-pointer hover:bg-indigo-50 p-2 rounded transition-colors">
                              <input
                                type="checkbox"
                                checked={newManagerPermissions.canViewTeamAttendance}
                                onChange={(e) => setNewManagerPermissions({
                                  ...newManagerPermissions,
                                  canViewTeamAttendance: e.target.checked
                                })}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              />
                              <span className="text-sm text-gray-700">Team Attendance</span>
                            </label>

                            <label className="flex items-center gap-2 cursor-pointer hover:bg-indigo-50 p-2 rounded transition-colors">
                              <input
                                type="checkbox"
                                checked={newManagerPermissions.canViewPerformance}
                                onChange={(e) => setNewManagerPermissions({
                                  ...newManagerPermissions,
                                  canViewPerformance: e.target.checked
                                })}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              />
                              <span className="text-sm text-gray-700">Performance</span>
                            </label>

                            <label className="flex items-center gap-2 cursor-pointer hover:bg-indigo-50 p-2 rounded transition-colors col-span-2">
                              <input
                                type="checkbox"
                                checked={newManagerPermissions.canViewWorkload}
                                onChange={(e) => setNewManagerPermissions({
                                  ...newManagerPermissions,
                                  canViewWorkload: e.target.checked
                                })}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              />
                              <span className="text-sm text-gray-700">Workload Management</span>
                            </label>
                          </div>
                        </div>
                        
                        <p className="mt-3 text-xs text-indigo-600 bg-indigo-50 p-2 rounded">
                          💡 Select which tabs this manager can access for this employee
                        </p>
                      </div>
                    </div>
                  )}
                    </div>
                  )}
                </>
              )}
            </form>
          </div>

          {/* Modal Footer - Modern Design */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 sm:flex sm:flex-row-reverse gap-3 border-t-2 border-gray-200">
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={saving}
              className="w-full sm:w-auto inline-flex justify-center items-center px-6 py-3 border-2 border-transparent rounded-xl shadow-lg text-base font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-4 focus:ring-blue-300 transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {saving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  {mode === 'create' ? '✨ Create User' : '💾 Update User'}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 sm:mt-0 w-full sm:w-auto inline-flex justify-center items-center px-6 py-3 border-2 border-gray-300 rounded-xl shadow-sm text-base font-semibold text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-gray-200 transition-all duration-200"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {teamLeadBlockOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
          <div
            className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm"
            onClick={() => setTeamLeadBlockOpen(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-lg p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Cannot Remove Team Lead</h3>
                <p className="text-sm text-gray-600 mt-1">
                  This user still has assigned team members. Please remove or reassign them before unchecking Team Lead.
                </p>
              </div>
              <button
                onClick={() => setTeamLeadBlockOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {teamLeadBlockError && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                {teamLeadBlockError}
              </div>
            )}

            <div className="mt-4">
              {teamLeadBlockLoading ? (
                <div className="flex items-center text-sm text-gray-600">
                  <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-gray-700 rounded-full mr-2"></div>
                  Loading assigned team members...
                </div>
              ) : teamLeadBlockSubordinates.length > 0 ? (
                <ul className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {teamLeadBlockSubordinates.map((member) => (
                    <li key={member.id} className="px-3 py-2 text-sm text-gray-700">
                      <div className="font-semibold text-gray-900">{member.name}</div>
                      <div className="text-xs text-gray-500">{member.email}</div>
                      {member.department && (
                        <div className="text-xs text-gray-500">{member.department}</div>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-gray-600">No assigned team members found.</div>
              )}
            </div>

            <div className="mt-5 flex justify-end">
              <button
                onClick={() => setTeamLeadBlockOpen(false)}
                className="px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserModal;
